import { useTranslation } from '@/i18n';
import { ModalWithoutPortal } from '@moneyforward/ap-frontend-components';
import { FC, memo, useCallback, useRef } from 'react';
import { Footer } from './Footer';
import { SelectedType } from './type';

type Children<T> = {
  onChange: (value: SelectedType, args?: T | undefined) => void;
};

export type Props<T> = {
  open: boolean;
  onClose: VoidFunction;
  onClick: (value: SelectedType, args: T | undefined) => void;
  children: FC<Children<T>>;
  testId?: string;
  defaultValue?: SelectedType;
  defaultArgs?: T;
};

const InnerDialog = <T,>({
  open,
  onClose,
  onClick,
  children,
  testId,
  defaultValue,
  defaultArgs,
}: Props<T>) => {
  const otherArgs = useRef<T | undefined>(defaultArgs);
  const selectedValue = useRef<SelectedType | undefined>(defaultValue);
  const onChange = useCallback((value: SelectedType, args: T | undefined) => {
    selectedValue.current = value;
    otherArgs.current = args;
  }, []);
  const onWrapClick = useCallback(() => {
    if (selectedValue.current) {
      onClick(selectedValue.current, otherArgs.current);
    }
  }, [onClick]);
  const { t } = useTranslation();
  return (
    <ModalWithoutPortal
      testId={`change-${testId}`}
      open={open}
      onClose={onClose}
      footer={
        <Footer
          onCancelClick={onClose}
          onPrimaryClick={onWrapClick}
          isDisabledPrimaryButton={selectedValue === undefined}
        />
      }
      title={t('invoice_reports.change_range_selection')}
      size='md'
    >
      {children({ onChange })}
    </ModalWithoutPortal>
  );
};
InnerDialog.displayName = 'ChangeXXX';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const Dialog = memo(InnerDialog) as typeof InnerDialog;
